/**
 * BoM management — direct bill of an assembly SKU, and where-used lookup.
 *
 * URL-addressable for deep links from MRP and elsewhere:
 *
 *   /bom?sku=FI-3C-N-TLA-A   → bill of this assembly SKU
 *   /bom?cpn=C-12345         → "where is this component used?"
 *
 * The Forecast page links here with `?cpn=`, the Build plan and Assemblies
 * pages link here with `?sku=`. Both modes can be visible at the same time,
 * which is handy when you are cross-checking an assembly against the components
 * it should be driving.
 *
 * The table is the bill of the SKU in the URL. A manufactured line can be
 * expanded to show that subassembly's own bill, nested under it.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";

import { apiFetch, ApiError } from "@/lib/api";
import { preserveRowOnCollapse } from "@/lib/preserveRowOnCollapse";
import { directBomRows } from "@/pages/bom/directBomRows";
import { MrpSkuCatalogHoverHintBody } from "@/components/mrp/MrpSkuCatalogHoverHintBody";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Input";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { formatNumber, normalizeSkuSearchInput, type MrpSkuHoverHintFields } from "@/lib/mrp";

interface BomRow {
  finished_good_sku: string;
  template_id: number | null;
  revision: string | null;
  variable_name: string | null;
  quantity: number;
  component_sku: string;
  manufacture: boolean;
}

interface BomResponse {
  sku: string;
  rows: BomRow[];
}

interface WhereUsedRow {
  assembly_id: number;
  assembly_code: string;
  assembly_display_name: string;
  location_code: string;
  sku: string;
  qty_per: number;
}

interface WhereUsedResponse {
  cpn: string;
  rows: WhereUsedRow[];
}

function BomSkuHoverHintCell({
  sku,
  hint,
  hintsLoading,
  onHoverIntent,
}: {
  sku: string;
  hint: MrpSkuHoverHintFields | undefined;
  hintsLoading: boolean;
  onHoverIntent: () => void;
}) {
  return (
    <HoverHintTrigger
      align="start"
      block
      trigger={
        <span
          className="block min-w-0 cursor-default font-mono text-xs text-sky-800 underline decoration-sky-800/40"
          onPointerEnter={onHoverIntent}
          onFocus={onHoverIntent}
        >
          {sku}
        </span>
      }
    >
      <MrpSkuCatalogHoverHintBody sku={sku} hint={hint} loading={hintsLoading} />
    </HoverHintTrigger>
  );
}

function useDeferredMrpSkuHoverHints(skus: string[], scope: string) {
  const uniqueSkus = useMemo(
    () => [...new Set(skus.map((sku) => sku.trim()).filter((sku) => sku.length > 0))],
    [skus],
  );
  const [hintsFetchEnabled, setHintsFetchEnabled] = useState(false);
  const enableHintsFetch = useCallback(() => {
    setHintsFetchEnabled(true);
  }, []);

  useEffect(() => {
    if (uniqueSkus.length === 0) {
      return;
    }
    const idleId =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback(() => setHintsFetchEnabled(true))
        : window.setTimeout(() => setHintsFetchEnabled(true), 0);
    return () => {
      if (typeof cancelIdleCallback === "function" && typeof idleId === "number") {
        cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId as number);
      }
    };
  }, [uniqueSkus]);

  const skuHoverHintsQuery = useQuery<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }, ApiError>({
    queryKey: ["mrp", "sku-hover-hints", scope, uniqueSkus.join("\n")],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: uniqueSkus.join(",") },
      }),
    enabled: hintsFetchEnabled && uniqueSkus.length > 0,
    staleTime: 60_000,
  });

  const hintsLoading =
    hintsFetchEnabled && (skuHoverHintsQuery.isPending || skuHoverHintsQuery.isFetching);

  return {
    skuHoverHints: skuHoverHintsQuery.data?.sku_hover_hints,
    hintsLoading,
    enableHintsFetch,
  };
}

export function BomBrowser() {
  const [searchParams, setSearchParams] = useSearchParams();
  const skuParam = searchParams.get("sku") ?? "";
  const cpnParam = searchParams.get("cpn") ?? "";

  const [skuInput, setSkuInput] = useState(skuParam);
  const [cpnInput, setCpnInput] = useState(cpnParam);

  // Keep local inputs in sync with URL changes (eg. when navigating in from
  // the Forecast page with `?cpn=...`).
  useEffect(() => setSkuInput(skuParam), [skuParam]);
  useEffect(() => setCpnInput(cpnParam), [cpnParam]);

  const updateParams = (next: { sku?: string; cpn?: string }): void => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(next)) {
      if (value && value.trim()) {
        params.set(key, normalizeSkuSearchInput(value));
      } else {
        params.delete(key);
      }
    }
    setSearchParams(params, { replace: false });
  };

  const bomQuery = useQuery<BomResponse, ApiError>({
    queryKey: ["mrp", "bom", "explode", skuParam],
    queryFn: () => apiFetch<BomResponse>("/api/mrp/bom", { query: { sku: skuParam } }),
    enabled: !!skuParam,
    refetchOnWindowFocus: false,
  });

  const whereUsedQuery = useQuery<WhereUsedResponse, ApiError>({
    queryKey: ["mrp", "bom", "where-used", cpnParam],
    queryFn: () => apiFetch<WhereUsedResponse>("/api/mrp/where-used", { query: { cpn: cpnParam } }),
    enabled: !!cpnParam,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">BoM</h1>
        <p className="text-sm text-slate-600">
          Show the bill of materials for an assembly SKU, or look up which assemblies consume a component. Open a
          manufactured line to see that subassembly&apos;s own bill underneath it.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Assembly SKU</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                updateParams({ sku: skuInput });
              }}
            >
              <div className="min-w-0 grow">
                <Label htmlFor="bom-explode-sku">SKU</Label>
                <AssemblySkuCombobox
                  id="bom-explode-sku"
                  className="w-full"
                  value={skuInput}
                  onChange={setSkuInput}
                  onPickMasterRow={(row) => updateParams({ sku: row.sku })}
                  placeholder="Search or type assembly SKU…"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={!skuInput.trim()}>
                  Explode
                </Button>
                {skuParam ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setSkuInput("");
                      updateParams({ sku: "" });
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Where used (component CPN)</CardTitle>
          </CardHeader>
          <CardBody>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                updateParams({ cpn: cpnInput });
              }}
            >
              <div className="min-w-0 grow">
                <Label htmlFor="bom-where-used-cpn">CPN</Label>
                <AssemblySkuCombobox
                  id="bom-where-used-cpn"
                  className="w-full"
                  value={cpnInput}
                  onChange={setCpnInput}
                  onPickMasterRow={(row) => updateParams({ cpn: row.sku })}
                  placeholder="Search or type CPN…"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={!cpnInput.trim()}>
                  Look up
                </Button>
                {cpnParam ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setCpnInput("");
                      updateParams({ cpn: "" });
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </form>
          </CardBody>
        </Card>
      </div>

      {skuParam ? <BomExplosionCard query={bomQuery} sku={skuParam} /> : null}

      {cpnParam ? <WhereUsedCard query={whereUsedQuery} cpn={cpnParam} onShowSku={(sku) => updateParams({ sku })} /> : null}

      {!skuParam && !cpnParam ? (
        <Card>
          <CardBody>
            <p className="text-sm text-slate-500">
              Enter an assembly SKU above to see its bill of materials, or paste a CPN to see every assembly that consumes it.
            </p>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

export function BomExplosionCard({
  query,
  sku,
}: {
  query: ReturnType<typeof useQuery<BomResponse, ApiError>>;
  sku: string;
}) {
  const componentRows = useMemo(
    () => (query.data ? directBomRows(query.data.rows, query.data.sku) : []),
    [query.data],
  );

  if (query.isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-slate-500">Loading bill of materials for {sku}…</p>
        </CardBody>
      </Card>
    );
  }
  if (query.error) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-rose-700">{query.error.message}</p>
        </CardBody>
      </Card>
    );
  }
  if (!query.data) return null;

  return (
    <Card className="flex min-h-0 flex-col lg:max-h-[calc(100vh-14rem)]">
      <CardHeader>
        <CardTitle>
          {componentRows.length} components for {query.data.sku}
        </CardTitle>
      </CardHeader>
      <CardBody className="min-h-0 overflow-auto p-0 [overflow-anchor:none]">
        <BomBillTable rows={componentRows} />
      </CardBody>
    </Card>
  );
}

function BomBillTable({ rows }: { rows: BomRow[] }) {
  return (
    <table className="min-w-full text-sm">
      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-left text-xs uppercase tracking-wide text-slate-500 backdrop-blur-sm">
        <tr>
          <th className="bg-slate-50 px-3 py-2">Component SKU</th>
          <th className="bg-slate-50 px-3 py-2 text-right tabular-nums">Quantity</th>
          <th className="bg-slate-50 px-3 py-2">Variable</th>
          <th className="bg-slate-50 px-3 py-2">Manufacture?</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        <BomBillRows depth={0} rows={rows} />
        {rows.length === 0 ? (
          <tr>
            <td className="px-4 py-6 text-center text-slate-500" colSpan={4}>
              No BoM rows returned for this SKU.
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

function BomBillRows({ depth, rows }: { depth: number; rows: BomRow[] }) {
  const componentSkus = useMemo(() => rows.map((row) => row.component_sku), [rows]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    componentSkus,
    depth === 0 ? "bom-direct" : `bom-direct-depth-${depth}`,
  );

  return (
    <>
      {rows.map((row, index) => (
        <BomBillRow
          key={`${row.finished_good_sku}-${row.component_sku}-${row.variable_name ?? ""}-${index}`}
          depth={depth}
          enableHintsFetch={enableHintsFetch}
          hint={skuHoverHints?.[row.component_sku.trim()]}
          hintsLoading={hintsLoading}
          row={row}
        />
      ))}
    </>
  );
}

function BomBillRow({
  depth,
  enableHintsFetch,
  hint,
  hintsLoading,
  row,
}: {
  depth: number;
  enableHintsFetch: () => void;
  hint: MrpSkuHoverHintFields | undefined;
  hintsLoading: boolean;
  row: BomRow;
}) {
  const [open, setOpen] = useState(false);
  const childQuery = useQuery<BomResponse, ApiError>({
    queryKey: ["mrp", "bom", "explode", row.component_sku],
    queryFn: () => apiFetch<BomResponse>("/api/mrp/bom", { query: { sku: row.component_sku } }),
    enabled: open && row.manufacture,
    refetchOnWindowFocus: false,
  });
  const childRows = useMemo(
    () => (childQuery.data ? directBomRows(childQuery.data.rows, childQuery.data.sku) : []),
    [childQuery.data],
  );
  const indentRem = depth * 1.25;

  return (
    <>
      <tr className={depth > 0 ? "bg-slate-50/80 hover:bg-slate-100/70" : "hover:bg-slate-50/80"}>
        <td className="px-3 py-2">
          <div className="flex min-w-0 items-center gap-1" style={{ paddingLeft: `${indentRem}rem` }}>
            {row.manufacture ? (
              <button
                type="button"
                aria-expanded={open}
                aria-label={open ? `Collapse bill of ${row.component_sku}` : `Expand bill of ${row.component_sku}`}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                onClick={(event) => {
                  const row = event.currentTarget.closest("tr");
                  if (open && row instanceof HTMLElement) {
                    preserveRowOnCollapse(row, () => setOpen(false));
                    return;
                  }
                  setOpen(true);
                }}
              >
                {open ? (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronRight className="h-4 w-4" aria-hidden />
                )}
              </button>
            ) : (
              <span className="inline-block h-7 w-7 shrink-0" aria-hidden />
            )}
            <div className="min-w-0" onPointerEnter={enableHintsFetch}>
              <BomSkuHoverHintCell
                sku={row.component_sku}
                hint={hint}
                hintsLoading={hintsLoading}
                onHoverIntent={enableHintsFetch}
              />
            </div>
          </div>
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">{formatNumber(row.quantity)}</td>
        <td className="px-3 py-2 text-xs">{row.variable_name ?? "—"}</td>
        <td className="px-3 py-2 text-xs">{row.manufacture ? "yes" : "no"}</td>
      </tr>
      {open && row.manufacture ? (
        <BomSubassemblyRows
          childRows={childRows}
          depth={depth + 1}
          errorMessage={childQuery.error?.message ?? null}
          loading={childQuery.isLoading}
          parentSku={childQuery.data?.sku ?? row.component_sku}
        />
      ) : null}
    </>
  );
}

function BomSubassemblyRows({
  childRows,
  depth,
  errorMessage,
  loading,
  parentSku,
}: {
  childRows: BomRow[];
  depth: number;
  errorMessage: string | null;
  loading: boolean;
  parentSku: string;
}) {
  const indentRem = depth * 1.25;
  return (
    <>
      <tr className="bg-slate-50">
        <td className="px-3 py-1.5 text-xs text-slate-500" colSpan={4}>
          <span className="inline-block" style={{ paddingLeft: `${indentRem}rem` }}>
            Components of {parentSku}
          </span>
        </td>
      </tr>
      {loading ? (
        <tr>
          <td className="px-3 py-2 text-xs text-slate-500" colSpan={4}>
            <span className="inline-block" style={{ paddingLeft: `${indentRem}rem` }}>
              Loading bill of materials for {parentSku}…
            </span>
          </td>
        </tr>
      ) : errorMessage ? (
        <tr>
          <td className="px-3 py-2 text-xs text-rose-700" colSpan={4}>
            <span className="inline-block" style={{ paddingLeft: `${indentRem}rem` }}>
              {errorMessage}
            </span>
          </td>
        </tr>
      ) : childRows.length === 0 ? (
        <tr>
          <td className="px-3 py-2 text-xs text-slate-500" colSpan={4}>
            <span className="inline-block" style={{ paddingLeft: `${indentRem}rem` }}>
              No components on {parentSku}.
            </span>
          </td>
        </tr>
      ) : (
        <BomBillRows depth={depth} rows={childRows} />
      )}
    </>
  );
}

function WhereUsedCard({
  query,
  cpn,
  onShowSku,
}: {
  query: ReturnType<typeof useQuery<WhereUsedResponse, ApiError>>;
  cpn: string;
  onShowSku: (sku: string) => void;
}) {
  const physicalSkus = useMemo(() => query.data?.rows.map((row) => row.sku) ?? [], [query.data?.rows]);
  const { skuHoverHints, hintsLoading, enableHintsFetch } = useDeferredMrpSkuHoverHints(
    physicalSkus,
    "bom-where-used",
  );

  if (query.isLoading) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-slate-500">Looking up where {cpn} is used…</p>
        </CardBody>
      </Card>
    );
  }
  if (query.error) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-rose-700">{query.error.message}</p>
        </CardBody>
      </Card>
    );
  }
  if (!query.data) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {query.data.rows.length} (assembly, location) pair{query.data.rows.length === 1 ? "" : "s"} consume {query.data.cpn}
        </CardTitle>
      </CardHeader>
      <CardBody className="overflow-x-auto p-0">
        <div onPointerEnter={enableHintsFetch}>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Assembly</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Physical SKU</th>
              <th className="px-3 py-2 text-right">Qty per build</th>
              <th className="px-3 py-2 text-right">Open BoM</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {query.data.rows.map((row) => (
              <tr key={`${row.assembly_id}-${row.location_code}`}>
                <td className="px-3 py-2">
                  <div className="font-mono text-xs text-slate-900">{row.assembly_code}</div>
                  {row.assembly_display_name && row.assembly_display_name !== row.assembly_code ? (
                    <div className="text-xs text-slate-500">{row.assembly_display_name}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-xs uppercase">{row.location_code}</td>
                <td className="px-3 py-2">
                  <BomSkuHoverHintCell
                    sku={row.sku}
                    hint={skuHoverHints?.[row.sku.trim()]}
                    hintsLoading={hintsLoading}
                    onHoverIntent={enableHintsFetch}
                  />
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{formatNumber(row.qty_per)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="secondary" onClick={() => onShowSku(row.sku)}>
                    Open
                  </Button>
                </td>
              </tr>
            ))}
            {query.data.rows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-slate-500" colSpan={5}>
                  No assemblies in <code>mrp.assembly_sku</code> consume this CPN. If forecast demand is non-zero for it,
                  that is a sign the BoM source data is out of date.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        </div>
      </CardBody>
    </Card>
  );
}

